import { BuyerUI, BuyerStatus } from '@choco-admin/types';

import type {
  BuyersPageBuyerFragmentFragment,
  BuyersPageBuyerDetailsFragmentFragment,
} from '../../../generated';

export type GeneratedBuyerFragment =
  | BuyersPageBuyerFragmentFragment
  | BuyersPageBuyerDetailsFragmentFragment;

/**
 * Maps a generated GraphQL buyer fragment to the generic BuyerUI type from @choco-admin/types
 * This ensures consistency across all buyer data transformations and avoids code duplication
 */
export const mapToBuyerUI = (buyer: GeneratedBuyerFragment): BuyerUI => {
  // Type-safe check for properties that might not exist in search fragment
  const isDetailedBuyer = 'isTest' in buyer;
  const profileImage = 'profileImage' in buyer ? buyer.profileImage : undefined;

  return {
    id: buyer.id,
    name: buyer.name || '',
    displayName: buyer.displayName || null,
    city: buyer.city || null,
    country: buyer.country || null,
    referenceId: buyer.referenceId || null,
    fullAddress: buyer.fullAddress || '',
    deliveryComment: buyer.deliveryComment || null,
    status: (() => {
      if (!buyer.status) return null;
      return buyer.status === 'pending'
        ? BuyerStatus.PENDING_APPROVAL
        : BuyerStatus.ACTIVE;
    })(),
    isTest: isDetailedBuyer ? buyer.isTest || false : false,
    isPuppy:
      isDetailedBuyer && 'isPuppy' in buyer ? buyer.isPuppy || false : false,
    rolesEnabled:
      isDetailedBuyer && 'rolesEnabled' in buyer
        ? buyer.rolesEnabled || false
        : false,
    profileImage: profileImage?.url ? { url: profileImage.url } : null,
    createdAt: buyer.createdAt
      ? new Date(buyer.createdAt).toISOString()
      : new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    // Address details (only available in detailed fragment)
    latitude:
      isDetailedBuyer && 'addressLatitude' in buyer
        ? buyer.addressLatitude || null
        : null,
    longitude:
      isDetailedBuyer && 'addressLongitude' in buyer
        ? buyer.addressLongitude || null
        : null,
    utcOffset:
      isDetailedBuyer && 'addressUTCOffset' in buyer
        ? buyer.addressUTCOffset || null
        : null,
    addressSource:
      isDetailedBuyer && 'addressSource' in buyer
        ? buyer.addressSource || null
        : null,
  };
};

/**
 * Maps an array of generated buyer fragments to BuyerUI array
 */
export const mapToBuyerUIList = (
  buyers: GeneratedBuyerFragment[]
): BuyerUI[] => {
  return buyers.map(mapToBuyerUI);
};
